import React, {useContext, useMemo, useState} from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import "./VideoSelector.scss";
import {getStreamIdTitle} from "../../../../utils/getStreamIdTitle";
import {StreamDetailsContext} from "../../../../context/streamDetails.context";
import {RaceTypeEnum} from "../../../../context/currentRaceEvent.context";
import clsx from "clsx";
import {BroadcasterConfigModel} from "../../../../models/broadcasterConfigModel";


export interface AutocompleteOption {
    streamId: string;
    title: string;
}

export interface VideoSelectorProps {
    value: string | undefined;
    onChange: (streamId: string | undefined) => void;
    disabled?: boolean;
    raceType: RaceTypeEnum | undefined
    broadcasterRegionalConfig: BroadcasterConfigModel | undefined
    overlayMode?: boolean;
    isHovered?: boolean;
}

export const VideoSelector = (props: VideoSelectorProps) => {
    const streamDetails = useContext(StreamDetailsContext);
    const [inputValue, setInputValue] = useState("");

    const autocompleteOptions = useMemo((): AutocompleteOption[] | undefined => {
        if (streamDetails?.streamIds) {
            return streamDetails.streamIds.map(id => ({
                streamId: id,
                title: getStreamIdTitle(id) ?? id
            }));

        } else if (streamDetails?.replayStreams) {
            return streamDetails.replayStreams.streams.map(stream => ({
                streamId: stream.url,
                title: stream.name
            }));
        } else {
            return undefined;
        }
    }, [streamDetails?.streamIds, streamDetails?.replayStreams]);

    autocompleteOptions?.sort((stream1, stream2) =>
        (stream1.title > stream2.title) ? 1 : -1)
        .sort((stream1) => (stream1.title === "Event TV") ? -1 : 1)
        .sort((stream1) => (stream1.title === "Helicopter") ? -1 : 1)
        .sort((stream1) => (stream1.title === "UMPAPP") ? -1 : 1)
        .sort((stream1) => (stream1.title === "World Feed") ? -1 : 1);

    const currentOption = useMemo((): AutocompleteOption | null =>
            autocompleteOptions?.find(opt => opt.streamId === props.value) ?? null
        , [autocompleteOptions, props.value]);

    return (
        <div
            className={clsx("videoSelectorContainer", {
                'liveRaceView': props.raceType === RaceTypeEnum.live && props?.broadcasterRegionalConfig?.broadcasterLogoUrl,
                'overlay-mode': props.overlayMode,
                'is-visible': props.isHovered
            })}>
            {props?.broadcasterRegionalConfig?.broadcasterLogoUrl && props.raceType === RaceTypeEnum.live &&
                <div className="broadcasterImageWrapper">


                <span className="dot"></span>
                <span className="liveOn">Live on</span>
                <img
                    src={props?.broadcasterRegionalConfig?.broadcasterLogoUrl}
                    className="broadcasterLogo"
                    alt={`${props?.broadcasterRegionalConfig?.broadcasterName} logo`}
                />
            </div>
            }


            <Autocomplete<AutocompleteOption, undefined, boolean>
                sx={{
                    border: "1px solid white",
                    transform: props.overlayMode ? "none" : "skewX(-10deg)",
                    maxWidth: props.overlayMode ? "none" : "50%"
                }}
                value={currentOption}
                disabled={props.disabled}
                onChange={(event, newValue) => props.onChange(newValue?.streamId)}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                options={autocompleteOptions ?? []}
                getOptionLabel={o => o.title}
                isOptionEqualToValue={(option, value) => option.streamId === value.streamId}
                style={{width: 192}}
                disableClearable={true}
                noOptionsText={'No Video streams available in your region'}
                renderInput={(params) => (
                    <TextField {...params} variant="outlined"/>
                )}
            />
        </div>
    );
};
